// Tracking-system domain types: the compliance/insurance, deliverable,
// milestone, meeting, KPI, SOW, variation, and issue entities plus their
// create/update input shapes, response envelopes, and type guards.
// Mirrors backend/src/models/tracking.ts. Re-exported from types/index.ts
// so import sites are unaffected.

// ═══════════════════════════════════════════════════════════════════════════
// Tracking System
//
// Types mirror `backend/src/models/tracking.ts` (z.infer-equivalent), using a
// discriminated union so callers narrow on `item.type`.
// ═══════════════════════════════════════════════════════════════════════════

export type TrackingType =
  | "insurance"
  | "deliverable"
  | "milestone"
  | "meeting"
  | "kpi"
  | "kpi-measurement"
  | "kpi-report"
  | "sow"
  | "variation"
  | "issue";

export const TAB_LEVEL_TYPES = [
  "insurance",
  "deliverable",
  "milestone",
  "meeting",
  "kpi",
  "sow",
  "variation",
  "issue",
] as const;
export type TabLevelType = (typeof TAB_LEVEL_TYPES)[number];

/** Singular UI labels — used for CTAs ("Add {label}"). */
export const TRACKING_TYPE_LABELS_SINGULAR: Record<TabLevelType, string> = {
  insurance: "Insurance / Licence",
  deliverable: "Deliverable / Obligation",
  milestone: "Milestone",
  meeting: "Meeting",
  kpi: "KPI",
  sow: "SOW",
  variation: "Variation",
  issue: "Issue",
};

/**
 * Plural UI labels — used for list headings and section titles. Slash form
 * matches the product vocabulary ("Insurances / Licences",
 * "Deliverables / Obligations") and avoids the `&` entity-escape
 * trap in aria-labels.
 */
export const TRACKING_TYPE_LABELS: Record<TabLevelType, string> = {
  insurance: "Insurances / Licences",
  deliverable: "Deliverables / Obligations",
  milestone: "Milestones",
  meeting: "Meetings",
  kpi: "KPIs",
  sow: "Statements of Work",
  variation: "Variations",
  issue: "Issues",
};

/** Top-level compliance category. Drives which sub-fields apply. */
export type InsuranceCategory = "insurance" | "licence" | "accreditation";

export const INSURANCE_CATEGORY_LABELS: Record<InsuranceCategory, string> = {
  insurance: "Insurance",
  licence: "Licence",
  accreditation: "Accreditation",
};

/** Insurance sub-types. Used only when category = 'insurance'; multi-select.
 *  When `other` is selected, the user supplies a free-text name; when
 *  `workers-compensation` is selected, the user supplies the state /
 *  jurisdiction (workers comp is state-regulated in AU). */
export type InsuranceType =
  | "public-liability"
  | "professional-indemnity"
  | "workers-compensation"
  | "product-liability"
  | "cyber-liability"
  | "motor-vehicle"
  | "other";

export const INSURANCE_TYPE_LABELS: Record<InsuranceType, string> = {
  "public-liability": "Public Liability",
  "professional-indemnity": "Professional Indemnity",
  "workers-compensation": "Workers Compensation",
  "product-liability": "Product Liability",
  "cyber-liability": "Cyber Liability",
  "motor-vehicle": "Motor Vehicle",
  other: "Other",
};

/** Insurance status is derived on read from startDate + expiryDate —
 *  never stored. `upcoming` = startDate is still in the future (AEST). */
export type InsuranceStatus = "upcoming" | "current" | "expiring" | "expired";

export type DeliverableType = "deliverable" | "obligation";
export type DeliverableOwner = "supplier" | "customer" | "joint";

// ─── Issue enums (mirror backend Zod schemas in models/tracking.ts) ─────
export type IssueSeverity = "critical" | "high" | "medium" | "low";
export type IssueStatus =
  | "open"
  | "in_progress"
  | "awaiting_response"
  | "resolved"
  | "closed";
export type IssueCloseOutcome =
  | "resolved"
  | "superseded"
  | "withdrawn"
  | "unresolved_on_record";
export type IssueThreadEntryType =
  | "opened"
  | "comment"
  | "status_change"
  | "action_raised"
  | "action_done";
/** Action lifecycle derived on read from the action's due/done dates —
 *  never stored. See the shared parity fixtures under
 *  tests/shared/issueDerivation/. */
export type IssueActionState = "open" | "done" | "overdue";

/** Cross-register link target type. `kpi-measurement` is the canonical
 *  "KPI period" link — there is no separate kpi_period type. */
export type IssueLinkTargetType =
  | "deliverable"
  | "milestone"
  | "kpi"
  | "kpi-measurement";

/** One cross-register link on an issue. The composite (targetType, targetId)
 *  is the identity; duplicates are rejected by the backend schema. */
export interface IssueLink {
  targetType: IssueLinkTargetType;
  targetId: string;
}
export type DeliverableStatus =
  | "not-started"
  | "submitted"
  | "under-review"
  | "revision-requested"
  | "conditionally-accepted"
  | "awaiting-resubmission"
  | "accepted"
  | "rejected"
  | "closed";

/** Derived deliverable status — adds two derived states distinct from the
 *  stored 7-value enum:
 *  - `overdue`: past-due, no submission, still in `not-started`. Action
 *    queue: chase supplier.
 *  - `late`: stored as submitted or under-review, but submittedAt arrived
 *    after the due date. Action queue: customer review (delayed delivery). */
export type DeliverableDerivedStatus =
  | DeliverableStatus
  | "overdue"
  | "late";

// Mirrors backend LifecycleEventType in backend/src/models/tracking.ts — must stay in sync.
export type LifecycleEventType =
  | "submitted"
  | "under-review"
  | "revision-requested"
  | "conditionally-accepted"
  | "awaiting-resubmission"
  | "failed-acceptance"
  | "accepted"
  | "rejected"
  | "closed"
  | "reverted";

// Mirrors backend LifecycleEvent in backend/src/models/tracking.ts — must stay in sync.
export interface DeliverableLifecycleEvent {
  type: LifecycleEventType;
  /** YYYY-MM-DD effective date of the transition. */
  date: string;
  comment?: string;
  /** Increments on each fail-acceptance event; carries to its follow-up
   *  awaiting-resubmission row so the timeline can chip-label the pair. */
  attemptIndex?: number;
  /** ISO timestamp the transition was recorded by an actor. */
  recordedAt: string;
  recordedBy: string;
}

/** Intent-only shape for appending a NEW lifecycle event via the
 *  `lifecycleEvents` field of a deliverable PUT. The server owns provenance
 *  (it stamps recordedBy = actor, recordedAt = request time), so callers send
 *  intent only — type, business date, optional comment/attemptIndex — and
 *  must NOT send recordedAt/recordedBy. Mirrors the backend
 *  `LifecycleEventAppendInputSchema`. The full `DeliverableLifecycleEvent`
 *  (with provenance) is still used for stored events and for
 *  `lifecycleEventsReplace`, which preserves historical authorship. */
export type DeliverableLifecycleEventAppendInput = Pick<
  DeliverableLifecycleEvent,
  "type" | "date" | "comment" | "attemptIndex"
>;

/** Wire shape for one event inside `lifecycleEventsReplace`. Existing events
 *  are resent with their stored provenance; events added in the lifecycle
 *  editor carry none. Provenance is server-owned either way: the backend
 *  keeps the stored provenance for events it can match and stamps the actor
 *  on the rest. Mirrors the backend `LifecycleEventReplaceInputSchema`. */
export type DeliverableLifecycleEventReplaceInput =
  DeliverableLifecycleEventAppendInput &
    Partial<Pick<DeliverableLifecycleEvent, "recordedAt" | "recordedBy">>;

/** Stored milestone status — only these three values may be persisted.
 *  Display states (`due-soon`, `overdue`, `completed-late`) are derived in
 *  the frontend from stored status + plannedDate + actualDate + today;
 *  never persisted. Pairs with the backend `MilestoneStatusSchema`. */
export type MilestoneStoredStatus =
  | "upcoming"
  | "partially-completed"
  | "completed";

/** Display milestone status — what UI surfaces render. Computed by
 *  `derivePillStatus` (user-intent, sticks with stored) or
 *  `deriveUrgencyStatus` (slippage-aware) at the call site. The two
 *  derivations can disagree on `partially-completed` + overdue: the pill
 *  keeps showing partial intent while urgency surfaces (schedule cell,
 *  timeline node, number badge) report `overdue`. */
export type MilestoneDisplayStatus =
  | MilestoneStoredStatus
  | "due-soon"
  | "overdue"
  | "completed-late";

/** Lifecycle states a milestone progresses through, based on its four
 *  persisted timestamps. Mirrors `MilestoneDerivedStatus` in
 *  `utils/deriveMilestoneStatus.ts`; the duplication keeps `types` free of
 *  cross-module imports. Source of truth lives with the helper. */
export type MilestoneLifecycleStatus =
  | "upcoming"
  | "delivered"
  | "partially-accepted"
  | "accepted"
  | "paid";

/** Risk overlay rendered alongside a milestone's lifecycle pill. Derived on
 *  read from plannedDate + lifecycle timestamps + today; never persisted. */
export type MilestoneRiskOverlayKind = "late" | "delivered-late" | "due-soon";

/** v2 milestone display shape: lifecycle base + optional risk overlay.
 *  Mirrors the result of `deriveMilestoneDisplayStatus`. Consumers (status
 *  badge, grouped table) accept this alongside the legacy unions for
 *  backwards compatibility.
 *  `secondaryPill` is set to `"paid"` when a partial acceptance has been
 *  recorded alongside a payment — both pills render to show the full state. */
export interface MilestoneDisplayStatusV2 {
  lifecycle: MilestoneLifecycleStatus;
  riskOverlay?: MilestoneRiskOverlayKind;
  secondaryPill?: "paid";
}

export type RagRating = "red" | "amber" | "green";
export const RAG_LABELS: Record<RagRating, string> = {
  red: "Red",
  amber: "Amber",
  green: "Green",
};

export type RecurrenceFrequency =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annually";

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval?: number;
  startDate: string;
  endDate?: string;
}

/** Evidence link — matches backend LinkSchema. */
export interface EvidenceLink {
  label: string;
  url: string;
}

/** Stored meeting status. `overdue` and `due-soon` are derived on read. */
export type MeetingStatus = "scheduled" | "held" | "cancelled";

/** Derived meeting status — adds `overdue` and `due-soon` proximity states. */
export type MeetingDerivedStatus = MeetingStatus | "overdue" | "due-soon";

/**
 * Default reminder days (days BEFORE insurance expiry) pre-checked in the
 * form modal. Users may customise on a per-insurance basis.
 *
 * Tuned for QITC government contracts; supersedes an earlier
 * [30, 14, 7, 0] default set.
 *
 * Rationale:
 * - 90: large policies need 60+ days for renewal quotes
 * - 60: mid-range lead time for supplier action
 * - 30: standard renewal window
 * - 14: late-stage nudge
 * - 1:  last-chance remediation
 *
 * Deliberately omitted from the default set:
 * - 7: too close to 14 — adds noise without new signal
 * - 0: day-of-expiry is too late; supplier is technically in breach by then
 *
 * Backend validator accepts any non-negative int array, so customisation
 * is never blocked at the API level.
 */
export const DEFAULT_REMINDER_DAYS: readonly number[] = [60, 30, 14, 1];

/**
 * Full set of reminder offsets the insurance form exposes as checkboxes.
 * `DEFAULT_REMINDER_DAYS` is the pre-checked subset for new records; users
 * may opt into 90d via the form. Backend accepts any non-negative subset.
 */
export const ALL_REMINDER_DAYS: readonly number[] = [90, 60, 30, 14, 1];

/**
 * Days BEFORE expiry to send a reminder. Used by insurance reminderDays.
 * Not absolute dates, not days-after — days-before the expiryDate.
 */
export type ReminderDaysBeforeExpiry = number[];

/** UI labels for KPI fields — QITC Schedule 5 vocabulary. */
export const KPI_FIELD_LABELS = {
  measurementFrequency: "Measurement Period",
  reportingFrequency: "Reporting Period",
  reportDueOffset: "Report Due (days after period end)",
} as const;

// ─── TrackingItem base + discriminated variants ────────────────────────
// Public `TrackingItem` is a discriminated union:
// callers narrow via `switch(item.type)` or type guards, guaranteeing
// per-type field access is type-checked.

interface TrackingBase {
  id: string;
  organisationId: string;
  contractId: string;
  name: string;
  dueDate?: string;
  evidenceLinks?: EvidenceLink[];
  notes?: string;
  parentId?: string;
  occurrenceDate?: string;
  // Archival
  archivedAt?: string;
  archivedBy?: string;
  archiveReason?: string;
  // Audit
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface Insurance extends TrackingBase {
  type: "insurance";
  /** insurance | licence | accreditation. Drives sub-field applicability. */
  category: InsuranceCategory;
  /** One or more when category = 'insurance'. Undefined for licence/accreditation. */
  insuranceTypes?: InsuranceType[];
  /** Free-text subject when category = 'licence' or 'accreditation'. */
  subjectName?: string;
  provider: string;
  policyNumber?: string;
  /** Coverage value in AUD whole dollars. Optional. */
  coverageValue?: number;
  /** Contract clause this requirement is tied to (e.g. "QITC Schedule 5 cl. 7.1"). */
  contractClause?: string;
  /** Minimum coverage required by the contract (AUD whole dollars).
   *  Optional — some insurances (e.g. workers compensation) have no dollar
   *  minimum. Stored only when the user supplies a positive amount. */
  minimumLimit?: number;
  /** Free-text name supplied when `insuranceTypes` includes `other`. */
  otherInsuranceName?: string;
  /** State / jurisdiction supplied when `insuranceTypes` includes `workers-compensation`. */
  workersCompensationState?: string;
  startDate: string;
  expiryDate: string;
  reminderDays: ReminderDaysBeforeExpiry;
  renewedFromId?: string;
  /** Derived on read (not stored). Set by mapper when API returns insurance items. */
  derivedStatus?: InsuranceStatus;
}

export interface Deliverable extends TrackingBase {
  type: "deliverable";
  deliverableType: DeliverableType;
  owner: DeliverableOwner;
  description?: string;
  /** Clause-in-contract reference (e.g. "Clause 7.2"). */
  clauseReference?: string;
  milestoneId?: string;
  /** Optional FK to the SOW this deliverable belongs to. Missing means
   *  unattached. On Update, sending `null` clears the attachment, but the
   *  read shape never carries null (the field is simply absent). */
  sowId?: string;
  ragRating?: RagRating;
  recurrenceRule?: RecurrenceRule;
  status: DeliverableStatus;
  /** Server-authored: stamped on the first transition * → submitted.
   *  Cleared on skip-back transitions (* → in-progress / not-started)
   *  per the stamping/clearing matrix. Null on legacy
   *  records and pre-submission states. Read-only from the client. */
  submittedAt?: string | null;
  /** Server-authored: stamped on the first transition * → accepted.
   *  Cleared when acceptance is reverted. Null on legacy records and
   *  records that have never been accepted. Read-only from the client. */
  acceptedAt?: string | null;
  /** Server-authored: stamped on the first transition * → rejected.
   *  Null on records that have never been rejected. Read-only from the
   *  client. */
  rejectedAt?: string | null;
  // Per-transition audit Recorded fields (submitted/accepted/rejected
  // RecordedAt/By) were dropped with the legacy status path — per-event
  // `recordedAt`/`recordedBy` inside `lifecycleEvents` is the provenance and
  // no component rendered the columns.
  /** Captured at rejection time — required by the backend when transitioning
   *  to `rejected`, persisted alongside the audit fields. Read-only from the
   *  client after the rejection write completes. */
  rejectionReason?: string | null;
  /** Optional per-stage free-text notes captured when the user
   *  transitions the deliverable through that lifecycle event.
   *  Editable after the fact via the standard PUT path. Surfaced
   *  read-only inline alongside the matching timestamp in the
   *  deliverable inline expand. Mirrors the milestone per-stage
   *  comment pattern. */
  submittedComment?: string;
  acceptedComment?: string;
  rejectedComment?: string;
  /** Server-authored: count of `* → submitted` transitions across the
   *  deliverable's lifecycle. Increments on the first submit and on each
   *  re-submission after a revision-requested cycle. Absent on legacy
   *  records that pre-date submission tracking — treat as 0. */
  submissionCount?: number;
  /** Source of `ragRating` on read. `manual` = user-entered; `derived` =
   *  computed from status/timing. Absent on legacy reads is treated as
   *  `manual` so existing stored ratings keep their meaning. */
  ragSource?: "manual" | "derived";
  /** Append-only audit log of lifecycle transitions. Backend canonical;
   *  status is derived on read. Absent on legacy records. */
  lifecycleEvents?: DeliverableLifecycleEvent[];
  /** Server-authored: stamped at the first `* → conditionally-accepted`
   *  transition. Read-only from the client. Null on records that have
   *  never been conditionally accepted. */
  conditionallyAcceptedAt?: string | null;
  /** Server-authored: stamped each time the deliverable enters the
   *  `awaiting-resubmission` resting state (latest wins). Read-only
   *  from the client. */
  awaitingResubmissionSince?: string | null;
  /** Server-derived: count of `failed-acceptance` events across the
   *  lifecycle log. Absent on legacy records — treat as 0. */
  failedAcceptanceCount?: number;
}

export interface Milestone extends TrackingBase {
  type: "milestone";
  milestoneNumber: number;
  /** Optional single-letter insertion suffix; "13a" = number 13 + suffix "a".
   *  Absent on every pre-existing milestone. */
  milestoneSuffix?: string;
  plannedDate: string;
  /** ISO date the supplier delivered the milestone. Server-authored on transition. */
  deliveredAt?: string | null;
  /** ISO date the agency issued partial acceptance. Server-authored on transition. */
  partiallyAcceptedAt?: string | null;
  /** ISO date the agency issued full acceptance. Server-authored on transition. */
  acceptedAt?: string | null;
  /** ISO date payment was confirmed. Server-authored on transition. */
  paidAt?: string | null;
  /** Optional free-text note captured when the user marked this milestone
   *  delivered. Surfaced inline beside the Delivered date in the detail
   *  popout. Sending null on Update clears a previously-stored note. */
  deliveredComment?: string | null;
  partiallyAcceptedComment?: string | null;
  acceptedComment?: string | null;
  paidComment?: string | null;
  paymentAmount?: number;
  /** Contract-language summary of what the milestone delivers. */
  description?: string;
  /** Definition of done / evidence required for acceptance. */
  acceptanceCriteria?: string;
  /** Optional FK to the SOW this milestone belongs to. Missing means
   *  unattached. On Update, sending `null` clears the attachment, but the
   *  read shape never carries null (the field is simply absent). */
  sowId?: string;
}

export interface Meeting extends TrackingBase {
  type: "meeting";
  meetingType: string;
  description?: string;
  /** Free-text reference to the contract clause/schedule that mandates this meeting. */
  contractReference?: string;
  date: string;
  /** Free text; may later be promoted to PersonReference[] for reminder delivery. */
  attendees?: string[];
  recurrenceRule?: RecurrenceRule;
  /** Stored meeting lifecycle. Defaults to 'scheduled' on legacy reads. */
  status: MeetingStatus;
  /** Set when status='held'. Refinement enforces pairing on backend. */
  heldDate?: string;
  /** Deprecated, retained for legacy display only — not editable. */
  frequency?: string;
  /** Deprecated, retained for legacy display only — not editable. */
  minutesLink?: string;
}

/** Ownership flag for contract-manager scorecards. `null` / undefined on
 *  legacy KPIs — drawer meta line hides the segment in that case. */
export type KpiOwnership = "supplier" | "customer" | "joint";

/** Value type for a KPI. `percentage` / `number` drive the numeric
 *  auto-RAG + amber-band branch; `boolean` short-circuits to green / red only
 *  based on the canonical strings below. Null / undefined on legacy KPIs
 *  falls back to percentage behaviour. */
export type KpiValueType = "percentage" | "number" | "boolean";

/** Canonical stored value for a boolean KPI measurement. Lowercase so CSV
 *  round-trips cleanly; `computeAutoRag` tolerates mixed case on read. */
export const KPI_BOOLEAN_MET = "met";
export const KPI_BOOLEAN_NOT_MET = "not_met";

export interface Kpi extends TrackingBase {
  type: "kpi";
  kpiNumber: number;
  definition: string;
  target: string;
  measurementFrequency: string;
  reportingFrequency: string;
  reportDueOffset?: number;
  /** Direction of improvement. Required on Create; optional on legacy KPIs. */
  higherIsBetter?: boolean;
  isSla?: boolean;
  /** Free-text SLA formula — display only, NEVER evaluated. */
  serviceCreditFormula?: string;
  /** Clause reference for the KPI. Backend populates this on
   *  read from either the new column OR the legacy `slaClauseReference`
   *  (new wins when both are set). Prefer this in new UI code. */
  kpiClauseReference?: string;
  /** Deprecated — kept for back-compat with records written before the rename.
   *  New writers should emit `kpiClauseReference`; backend accepts both. */
  slaClauseReference?: string;
  ownership?: KpiOwnership | null;
  /** Per-KPI amber-band override (decimal, e.g. 0.10 = ±10%). Null / undefined
   *  falls back to `AMBER_BAND_RELATIVE` in `lib/kpi/autoRag`. Max 0.5
   *  mirrors the backend Zod guard. */
  amberBandPercent?: number | null;
  /** Value type. Null / undefined = legacy percentage KPI. */
  valueType?: KpiValueType | null;
  /** Free-text unit (e.g. "hrs", "incidents", "$"). Only meaningful when
   *  `valueType === "number"`; ignored for percentage / boolean KPIs. */
  unit?: string | null;
}

export interface KpiMeasurement extends TrackingBase {
  type: "kpi-measurement";
  parentId: string;
  period: string;
  value: string;
  date: string;
}

export interface KpiReport extends TrackingBase {
  type: "kpi-report";
  parentId: string;
  period: string;
  ragRating: RagRating;
  commentary?: string;
  evidenceLinks?: EvidenceLink[];
}

/** Lifecycle status for a Statement of Work. User-driven transitions —
 *  a SOW with an end date in the past can still be `active` if work has
 *  not been formally closed. The brief slippage cue lives in the period
 *  cell meta-line, not the pill. */
export type SowStatus = "draft" | "active" | "completed" | "cancelled";

/** Audit-row appended to a SOW's correction history when an admin uses the
 *  dedicated correction endpoint to repair an originalValue typo. Stored as
 *  an array on the SOW; surfaced read-only in the inline expand for admins. */
export interface OriginalValueCorrection {
  at: string;
  by: string;
  from: number;
  to: number;
  reason: string;
}

/** Statement of Work. Scoped piece of work commissioned
 *  under a master contract. `originalValue` is locked at create time;
 *  value changes flow through approved variations. The audit-
 *  logged correction endpoint is the only path for typo repair. */
export interface Sow extends TrackingBase {
  type: "sow";
  /** Format: SOW-{YYYY}-{NNN}. Auto-generated on create with manual override.
   *  Immutable after create — number is read-only display in inline edit. */
  number: string;
  description?: string;
  startDate: string;
  endDate: string;
  /** Stored ex-GST in AUD whole dollars. UI labels the basis on display. */
  originalValue: number;
  status: SowStatus;
  originalValueCorrections?: OriginalValueCorrection[];
}

/** Approval lifecycle for a variation. Both `approved` and `rejected` are
 *  terminal — to resurrect a rejected variation, create a new one. */
export type VariationStatus = "proposed" | "approved" | "rejected";

/** Polymorphic parent type for a variation. `contract` = the variation
 *  modifies the master contract directly; `sow` = the variation modifies
 *  a specific SOW under the contract.
 *
 *  Retained for legacy read-compatibility: pre-multi-target variations
 *  store this on the wire. Frontend reads synthesise a single-element
 *  `targets[]` from `variesType` + `variesId` when `targets` is absent. */
export type VariesType = "contract" | "sow";

/** A single target of a multi-target variation. A variation may apply
 *  to the contract AND/OR multiple SOWs. Optional `allocation` partitions
 *  the variation's `valueImpact` to this target — null/undefined means
 *  unallocated. When any target carries an allocation, ALL must (and the
 *  sum must equal `valueImpact`). */
export type VariationTarget =
  | { kind: "contract"; allocation?: number | null }
  | { kind: "sow"; sowId: string; allocation?: number | null };

/** Variation. Multi-target — the variation may
 *  apply to the contract AND/OR multiple SOWs simultaneously. `valueImpact`
 *  is the total in AUD whole dollars ex-GST; null = non-financial;
 *  negative = descope. Per-target `allocation` is optional; when omitted
 *  the variation has a total but no partition.
 *
 *  Status is a three-state machine (proposed → approved | rejected, both
 *  terminal). Status transitions go through dedicated approve / reject
 *  endpoints, not the standard PUT path.
 *
 *  Legacy compatibility: the legacy `variesType` + `variesId` fields are
 *  retained on the read shape to support synthesising `targets` for older
 *  records. Always prefer `targets` when present. */
export interface Variation extends TrackingBase {
  type: "variation";
  /** Format: VAR-{YYYY}-{NNN}. Auto-generated on create with manual
   *  override. Immutable after create. */
  number: string;
  description?: string;
  /** Multi-target list. Always at least one entry on new variations.
   *  Optional during the transition window — a backend response may
   *  omit `targets` for legacy records, in which case the read path
   *  synthesises a single-element list from `variesType` + `variesId`. */
  targets?: VariationTarget[];
  /** @deprecated Legacy single-target field. Read-only for compatibility
   *  with legacy single-target stored records; new code reads `targets`. */
  variesType?: VariesType;
  /** @deprecated Legacy single-target field. */
  variesId?: string;
  /** Optional — backend allows a Proposed variation to be created
   *  without a finalised effective date (the /approve handler enforces
   *  presence at decision time). Frontend code that reads this field
   *  must handle undefined or be confined to a status-gated context
   *  that has already verified presence. */
  effectiveDate?: string;
  /** Null = non-financial. Backend rejects an explicit zero — use null
   *  for variations that have no monetary effect. */
  valueImpact: number | null;
  status: VariationStatus;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectedReason?: string;
}

/** One append-only entry in an issue's discussion thread. `opened`,
 *  `comment` and `status_change` carry text / from-to context;
 *  `action_raised` additionally carries a committed action (description,
 *  owning party, optional internal owner, due date), and the paired
 *  `action_done` entry references the raising entry via `raisingEntryId`. */
export interface IssueThreadEntry {
  entryId: string;
  type: IssueThreadEntryType;
  authorId: string;
  createdAt: string;
  text?: string;
  statusFrom?: IssueStatus;
  statusTo?: IssueStatus;
  actionDescription?: string;
  actionParty?: DeliverableOwner;
  actionOwnerId?: string;
  actionDueDate?: string;
  actionDoneAt?: string;
  raisingEntryId?: string;
}

/** A logged contract issue with a chronological thread, committed actions,
 *  and a five-state workflow (open → in_progress / awaiting_response →
 *  resolved → closed; closed is terminal, resolved → open re-opens).
 *
 *  `title` is the canonical UI field; the inherited generic `name` mirrors it
 *  for activity-log hooks and the uniform tracking shape. `party` records
 *  which side the issue concerns (label only — there are no supplier logins).
 *  `statusEnteredAt` drives the derived duration-in-state. `actions` is the
 *  denormalised action register keyed by the raising thread entry id; each
 *  action's state and lateness are derived on read from its dates. */
export interface Issue extends TrackingBase {
  type: "issue";
  /** ISS-NNN. Allocated server-side on create. */
  issueRef: string;
  title: string;
  description: string;
  severity: IssueSeverity;
  status: IssueStatus;
  party: DeliverableOwner;
  ownerId?: string;
  /** Free-text contract pointer (e.g. "Clause 5.3") — distinct from the
   *  structured linked-items picker. */
  contractReference?: string;
  raisedAt: string;
  /** Drives the derived duration-in-state indicator. */
  statusEnteredAt: string;
  resolvedAt?: string;
  closedAt?: string;
  closeOutcome?: IssueCloseOutcome;
  outcomeNote?: string;
  threadEntries?: IssueThreadEntry[];
  /** Denormalised action register, keyed by the raising thread entry id. */
  actions?: Record<
    string,
    {
      description: string;
      party: DeliverableOwner;
      ownerId?: string;
      dueDate: string;
      raisedAt: string;
      doneAt?: string;
    }
  >;
  /** Cross-register links — issues reference the deliverables, milestones,
   *  KPI series or specific KPI measurements they concern. Composite-unique
   *  by (targetType, targetId). Free-text `contractReference` above
   *  coexists. */
  links?: IssueLink[];
}

export type TrackingItem =
  | Insurance
  | Deliverable
  | Milestone
  | Meeting
  | Kpi
  | KpiMeasurement
  | KpiReport
  | Sow
  | Variation
  | Issue;

// ─── Type guards (co-located with the discriminated union) ─────────────

export const isInsurance = (i: TrackingItem): i is Insurance =>
  i.type === "insurance";
export const isDeliverable = (i: TrackingItem): i is Deliverable =>
  i.type === "deliverable";
export const isMilestone = (i: TrackingItem): i is Milestone =>
  i.type === "milestone";
export const isMeeting = (i: TrackingItem): i is Meeting => i.type === "meeting";
export const isKpi = (i: TrackingItem): i is Kpi => i.type === "kpi";
export const isKpiMeasurement = (i: TrackingItem): i is KpiMeasurement =>
  i.type === "kpi-measurement";
export const isKpiReport = (i: TrackingItem): i is KpiReport =>
  i.type === "kpi-report";
export const isSow = (i: TrackingItem): i is Sow => i.type === "sow";
export const isVariation = (i: TrackingItem): i is Variation =>
  i.type === "variation";
export const isIssue = (i: TrackingItem): i is Issue => i.type === "issue";

// ─── Create / Update input types ───────────────────────────────────────

interface TrackingBaseCreate {
  name: string;
  contractId: string;
  dueDate?: string;
  evidenceLinks?: EvidenceLink[];
  notes?: string;
  parentId?: string;
  occurrenceDate?: string;
}

/** Insurance omits `name` (derived server-side from category + types/subjectName). */
interface InsuranceBaseCreate extends Omit<TrackingBaseCreate, "name"> {
  name?: string;
}

export interface CreateInsuranceInput extends InsuranceBaseCreate {
  category: InsuranceCategory;
  /** Required (min 1) when category = 'insurance'. */
  insuranceTypes?: InsuranceType[];
  /** Required when category = 'licence' or 'accreditation'. */
  subjectName?: string;
  provider: string;
  policyNumber?: string;
  /** Coverage value in AUD whole dollars. Optional. */
  coverageValue?: number;
  /** Contract clause this requirement is tied to. Optional. */
  contractClause?: string;
  /** Minimum coverage required by the contract (AUD). Optional. */
  minimumLimit?: number;
  /** Free-text name. Required when insuranceTypes includes 'other'. */
  otherInsuranceName?: string;
  /** State / jurisdiction. Required when insuranceTypes includes 'workers-compensation'. */
  workersCompensationState?: string;
  startDate: string;
  expiryDate: string;
  reminderDays: ReminderDaysBeforeExpiry;
  renewedFromId?: string;
}
export type UpdateInsuranceInput = Partial<CreateInsuranceInput> & {
  version: number;
};

export interface CreateDeliverableInput extends TrackingBaseCreate {
  deliverableType: DeliverableType;
  owner: DeliverableOwner;
  description?: string;
  clauseReference?: string;
  milestoneId?: string;
  /** SOW attachment. `string` attaches; `null` on Update clears the
   *  attachment server-side; omitted (undefined) is the no-change
   *  signal. Backend Update schema accepts `nullable().optional()`. */
  sowId?: string | null;
  ragRating?: RagRating;
  recurrenceRule?: RecurrenceRule;
  status: DeliverableStatus;
}
export type UpdateDeliverableInput = Partial<CreateDeliverableInput> & {
  version: number;
  /** Business effective date carried by the actor on submit / accept /
   *  reject. The server records its own wall-clock `recordedAt` plus
   *  this effective date as the business-meaningful timestamp.
   *  YYYY-MM-DD; backend rejects future dates. Ignored when the
   *  request does not transition status. */
  effectiveDate?: string;
  /** Required when transitioning to `rejected`. 1–2000 chars. */
  rejectionReason?: string;
  /** Delta of NEW lifecycle events to append. Intent-only — the server
   *  stamps provenance (recordedBy = actor, recordedAt = now) and the
   *  derived timestamps, so the client sends just type + business date
   *  (+ optional comment). Mirrors the backend
   *  `LifecycleEventAppendInputSchema`. Server uses
   *  `list_append(if_not_exists(...), :newEvents)` (race-safe); each event
   *  is guarded by the shared chain validator against the merged chain.
   *  Status is derived on read from the resulting log. */
  lifecycleEvents?: DeliverableLifecycleEventAppendInput[];
  /** Mirrors backend lifecycleEventsReplace in backend/src/models/tracking.ts —
   *  must stay in sync. Distinct from lifecycleEvents (delta append): this
   *  REPLACES the whole array. Mutually exclusive with lifecycleEvents and
   *  status on the same request. Backend validates chronological consistency
   *  and state-machine guards across the full replacement array. */
  lifecycleEventsReplace?: DeliverableLifecycleEventReplaceInput[];
};

export interface CreateMilestoneInput extends TrackingBaseCreate {
  milestoneNumber: number;
  /** Optional single-letter insertion suffix ("13a"). `null` on Update is the
   *  explicit clear signal (13a → 13); omitted is the no-change signal. Create
   *  only ever sends a letter or omits. */
  milestoneSuffix?: string | null;
  plannedDate: string;
  deliveredAt?: string | null;
  partiallyAcceptedAt?: string | null;
  acceptedAt?: string | null;
  paidAt?: string | null;
  paymentAmount?: number;
  description?: string;
  acceptanceCriteria?: string;
  /** SOW attachment. `string` attaches; `null` on Update clears the
   *  attachment server-side; omitted (undefined) is the no-change
   *  signal. Backend Update schema accepts `nullable().optional()`. */
  sowId?: string | null;
}
export type UpdateMilestoneInput = Partial<CreateMilestoneInput> & {
  version: number;
};

export interface CreateMeetingInput extends TrackingBaseCreate {
  meetingType: string;
  description?: string;
  contractReference?: string;
  date: string;
  attendees?: string[];
  recurrenceRule?: RecurrenceRule;
  status?: MeetingStatus;
  heldDate?: string;
}
export type UpdateMeetingInput = Partial<CreateMeetingInput> & {
  version: number;
};

export interface CreateKpiInput extends TrackingBaseCreate {
  kpiNumber?: number;
  definition: string;
  target: string;
  measurementFrequency: string;
  reportingFrequency: string;
  reportDueOffset?: number;
  higherIsBetter?: boolean;
  isSla?: boolean;
  serviceCreditFormula?: string;
  /** Preferred. Backend accepts either name on write. */
  kpiClauseReference?: string;
  /** Deprecated alias of `kpiClauseReference`. Prefer the new name. */
  slaClauseReference?: string;
  ownership?: KpiOwnership | null;
  amberBandPercent?: number | null;
  valueType?: KpiValueType | null;
  unit?: string | null;
}
export type UpdateKpiInput = Partial<CreateKpiInput> & { version: number };

export interface CreateKpiMeasurementInput extends TrackingBaseCreate {
  parentId: string;
  period: string;
  value: string;
  date: string;
}
export type UpdateKpiMeasurementInput = Partial<CreateKpiMeasurementInput> & {
  version: number;
};

export interface CreateKpiReportInput extends TrackingBaseCreate {
  parentId: string;
  period: string;
  ragRating: RagRating;
  commentary?: string;
}
export type UpdateKpiReportInput = Partial<CreateKpiReportInput> & {
  version: number;
};

/** Add SOW form payload. `number` is optional on create — the backend
 *  auto-generates `SOW-{YYYY}-{NNN}` when omitted; supplying a value
 *  triggers the manual-override path which is uniqueness-checked via the
 *  TRACKING_NUMBER sentinel item. `originalValue` is in AUD whole dollars,
 *  ex-GST. */
export interface CreateSowInput extends TrackingBaseCreate {
  number?: string;
  description?: string;
  startDate: string;
  endDate: string;
  originalValue: number;
  status?: SowStatus;
}

/** Edit SOW form payload. `originalValue` and `number` are immutable on
 *  the standard PUT path — the backend Zod refinement rejects either
 *  field. Typo repair for `originalValue` flows through the dedicated
 *  audit-logged correction endpoint. */
export type UpdateSowInput = Partial<
  Omit<CreateSowInput, "originalValue" | "number">
> & {
  version: number;
};

/** Body for POST .../tracking/sow/{id}/correct-original-value. Editor
 *  role required; reason min 10 chars so the audit row is meaningful. */
export interface CorrectOriginalValueInput {
  newOriginalValue: number;
  reason: string;
}

/** Body for POST .../tracking/sow/{id}/correct-status. Editor role
 *  required; reason min 10 chars. Bypasses the SOW state-machine guard
 *  for legitimate corrections (e.g. a clerical mis-cancellation). */
export interface CorrectStatusInput {
  newStatus: SowStatus;
  reason: string;
}

/** Body for POST .../tracking/variation/{id}/correct-variation. Editor
 *  role required; reason min 10 chars. At least one new* field must be
 *  supplied. Bypasses the variation state-machine status-gate so
 *  approved/rejected variations can have typos repaired. */
export interface CorrectVariationInput {
  newName?: string;
  newDescription?: string | null;
  newValueImpact?: number | null;
  newEffectiveDate?: string | null;
  reason: string;
}

/** Add Variation form payload. `number` is optional on create — backend
 *  auto-generates `VAR-{YYYY}-{NNN}` when omitted; supplying a value
 *  triggers the manual-override path uniqueness-checked via the
 *  TRACKING_NUMBER sentinel. `valueImpact` is null for non-financial
 *  variations; otherwise an integer (positive or negative) AUD
 *  ex-GST. `targets` must contain at least one entry. */
export interface CreateVariationInput extends TrackingBaseCreate {
  number?: string;
  description?: string;
  targets: VariationTarget[];
  effectiveDate: string;
  valueImpact: number | null;
  status?: VariationStatus;
}

/** Edit Variation form payload. `number` and `status` are rejected on
 *  the standard PUT path — status transitions go through approve /
 *  reject; the number is fixed at create. `targets` is editable while
 *  the variation is `proposed` (the backend Zod refinement gates this);
 *  approved or rejected variations lock the target list as part of the
 *  audit trail. */
export type UpdateVariationInput = Partial<
  Omit<CreateVariationInput, "number" | "status">
> & {
  version: number;
};

/** Body for POST .../tracking/variation/{id}/reject. Optional reason
 *  ≤500 chars. Backend `.strict()` rejects any other key. */
export interface RejectVariationInput {
  reason?: string;
}

/** Create Issue body — mirrors backend `CreateIssueSchema` (client-supplied
 *  fields only). `contractId` is supplied via the request path, not the body;
 *  the server stamps issueRef / status / statusEnteredAt / raisedAt /
 *  createdBy and the generic `name` (from `title`). Does NOT extend
 *  `TrackingBaseCreate` — the issue create shape uses `title` rather than
 *  `name` and carries no body `contractId`. */
export interface CreateIssueInput {
  title: string;
  description: string;
  severity: IssueSeverity;
  party: DeliverableOwner;
  contractReference?: string;
  ownerId?: string;
  /** Cross-register links — issues reference the deliverables, milestones,
   *  KPI series or specific KPI measurements they concern. Composite-unique
   *  by (targetType, targetId). */
  links?: IssueLink[];
}

/** Edit Issue form payload — metadata only. Mirrors backend
 *  `UpdateIssueSchema`: status and the lifecycle timestamps mutate through
 *  dedicated command endpoints, never PUT, so they are absent here. */
export type UpdateIssueInput = Partial<CreateIssueInput> & {
  version: number;
};

// ─── Issue command payloads ────────────────────────────────────────────
// Request bodies for the dedicated issue command endpoints (transition /
// close / thread append / action append / action-done). These mutate
// lifecycle + thread state outside the generic PUT, which only edits
// metadata. Each mirrors the backend command schema.

/** POST .../tracking/issue/{id}/transition — any status change, including
 *  →resolved (server stamps resolvedAt) and resolved→open re-open. Guarded
 *  server-side by canTransitionIssue; auto-logs a status_change thread
 *  entry. Resolving is just a transition with `to` 'resolved' — there is no
 *  separate resolve endpoint. `version` is the optimistic lock that makes
 *  the guard atomic against a concurrent change (409 on mismatch). */
export interface TransitionIssueInput {
  to: IssueStatus;
  note?: string;
  version: number;
}

/** POST .../tracking/issue/{id}/close — close with a required outcome reason
 *  and optional note. `version` is the optimistic lock (409 on mismatch). */
export interface CloseIssueInput {
  closeOutcome: IssueCloseOutcome;
  outcomeNote?: string;
  version: number;
}

/** UI-layer transition command — the composer / drawer collect the target +
 *  optional note; the orchestrator folds in the issue `version` to form the
 *  wire `TransitionIssueInput`. */
export type IssueTransitionCommand = Omit<TransitionIssueInput, "version">;

/** UI-layer close command — the dialog collects the outcome + optional note;
 *  the orchestrator folds in the issue `version` to form `CloseIssueInput`. */
export type IssueCloseCommand = Omit<CloseIssueInput, "version">;

/** POST .../tracking/issue/{id}/thread — append a free-text comment. */
export interface AppendThreadCommentInput {
  text: string;
}

/** POST .../tracking/issue/{id}/thread/action — raise a committed action
 *  (owned by a party + optional internal owner, with a due date). Field
 *  names carry the `action` prefix to match the backend schema. */
export interface AppendThreadActionInput {
  actionDescription: string;
  actionParty: DeliverableOwner;
  actionOwnerId?: string;
  actionDueDate: string;
  text?: string;
}

/** POST .../tracking/issue/{id}/thread/{entryId}/action-done — mark the
 *  action raised by {entryId} done. The server stamps the completion date
 *  and writes the paired action_done thread entry; the body is empty. */
export type MarkActionDoneInput = Record<string, never>;

export type CreateTrackingInput =
  | CreateInsuranceInput
  | CreateDeliverableInput
  | CreateMilestoneInput
  | CreateMeetingInput
  | CreateKpiInput
  | CreateKpiMeasurementInput
  | CreateKpiReportInput
  | CreateSowInput
  | CreateVariationInput
  | CreateIssueInput;

// ─── KPI period batch ──────────────────────────────────────────────────
// POST /manage/contracts/{contractId}/tracking/kpi-period-batch. Matches
// the backend `KpiPeriodBatchSchema` / per-item result union — partial-
// success is the contract (one bad row doesn't fail the batch).

export interface KpiBatchMeasurement {
  period: string;
  value: string;
  date: string;
}

export interface KpiBatchReport {
  period: string;
  ragRating: RagRating;
  commentary?: string;
  evidenceLinks?: EvidenceLink[];
}

export interface KpiBatchItem {
  kpiId: string;
  measurement?: KpiBatchMeasurement;
  report?: KpiBatchReport;
}

export interface KpiBatchRequest {
  items: KpiBatchItem[];
}

export type KpiBatchItemResult =
  | { ok: true; kpiId: string; measurementId?: string; reportId?: string }
  | { ok: false; kpiId: string; error: string };

export interface KpiBatchResponse {
  items: KpiBatchItemResult[];
}

export type UpdateTrackingInput =
  | UpdateInsuranceInput
  | UpdateDeliverableInput
  | UpdateMilestoneInput
  | UpdateMeetingInput
  | UpdateKpiInput
  | UpdateKpiMeasurementInput
  | UpdateKpiReportInput
  | UpdateSowInput
  | UpdateVariationInput
  | UpdateIssueInput;

// ─── Response shapes ───────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  items: T[];
  nextToken?: string;
}

export type TrackingItemListResponse = PaginatedResponse<TrackingItem>;

/**
 * Per-tab count response for both `/manage/contracts/{id}/tracking/counts`
 * and `/manage/tracking/summary`. Backend wraps each count in `{ total: N }`
 * so future sibling aggregates (e.g. `expiring`, `overdue`) can be added without
 * a wire-format change. Consumers read `.total` for the current total.
 *
 * `hasAttention` is emitted only by the per-contract counts endpoint (the tab
 * bar reads it to render a per-type attention dot); the org-wide summary
 * endpoint does not emit it, so the field is optional.
 */
export interface TrackingCountBucket {
  total: number;
  hasAttention?: boolean;
}
export interface TrackingCountsResponse {
  insurance: TrackingCountBucket;
  deliverable: TrackingCountBucket;
  milestone: TrackingCountBucket;
  meeting: TrackingCountBucket;
  kpi: TrackingCountBucket;
  /** Optional — backend may not yet emit a SOW bucket for legacy contracts.
   *  Consumers should `?? 0` on read. */
  sow?: TrackingCountBucket;
  /** Optional — legacy contracts without variations always read
   *  `undefined` here. */
  variation?: TrackingCountBucket;
  /** Optional — contracts with no issues read `undefined` here. */
  issue?: TrackingCountBucket;
  /** Invoices awaiting review (status 'review') — NOT a total invoice
   *  count. Optional because the backend omits the bucket when the finance
   *  read fails (the counts endpoint is fail-open on that read). */
  finance?: TrackingCountBucket;
}

export interface TrackingListParams {
  limit?: number;
  nextToken?: string;
  includeArchived?: boolean;
}

export interface ArchiveTrackingInput {
  reason: string;
}

export interface GenerateOccurrencesResponse {
  created: number;
  items: TrackingItem[];
}

/**
 * Dry-run preview of the generate-occurrences backend endpoint. Lets the
 * Generate Occurrences dialog show an exact count + date strip before the
 * user commits. `capped: true` means the recurrence would produce more
 * than 104 occurrences; the backend clamps at 104 for safety.
 */
export interface OccurrencePreviewResponse {
  count: number;
  occurrenceDates: string[];
  capped: boolean;
}
